import React, {useContext, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {
    Box,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url'
import {getParamsFromPath} from '@salesforce/retail-react-app/app/utils/site-utils'
import {usePage} from '@salesforce/commerce-sdk-react'
import LinkTile from '@consteed/overrides/page-designer/assets/link-tile'
import convertPageToNavigationTree from '@consteed/frontend/utils/page-to-nav-tree'
import {generalUrlBuilder} from '@consteed/overrides/utils/url'
import {
    SELECTED_ROLE_KEY,
    ROLE_RIDER,
    ROLE_VET,
    PRODUCT_IDS_RIDER,
    PRODUCT_IDS_VET
} from '@consteed/overrides/constants'
import {SELECT_ROLE_HEADLINE_LINE1} from '@consteed/overrides/i18n/components/SelectRoleModal'

const ALLOWED_ROLES = [ROLE_RIDER, ROLE_VET]

const setLinkTileRole = (linkTileConfig) => {
    let role
    if (linkTileConfig?.link?.indexOf(ROLE_RIDER) !== -1) {
        role = ROLE_RIDER
    } else if (linkTileConfig?.link?.indexOf(ROLE_VET) !== -1) {
        role = ROLE_VET
    } else if (linkTileConfig?.linkType === 'Product') {
        if (PRODUCT_IDS_RIDER.indexOf(linkTileConfig.id) !== -1) {
            role = ROLE_RIDER
        } else if (PRODUCT_IDS_VET.indexOf(linkTileConfig.id) !== -1) {
            role = ROLE_VET
        }
    }
    return {
        ...linkTileConfig,
        role
    }
}

/**
 * Updates the locale with the user's preferred locale if locale is not part of the URL.
 *
 * @param {Object} context - The multi-site context.
 * @param {Object} context.site - The site object.
 * @param {Function} context.updateLocale - The function to update the locale
 * @returns {boolean} if locale was updated
 */
const updateLocaleWithUserPreferredLocale = ({site, updateLocale}) => {
    const path = `${window.location.pathname}${window.location.search}`
    let {localeRef} = getParamsFromPath(path)
    // check if locale is in URL and if preferences are set
    if (!localeRef && window?.navigator?.languages) {
        let userPreferredLocale = null
        const userPreferredLocaleIds = [...window.navigator.languages]
        const {supportedLocales} = site.l10n

        userPreferredLocaleIds.find((userPreferredLocaleId) => {
            userPreferredLocale = supportedLocales.find(
                (locale) =>
                    locale.alias === userPreferredLocaleId || locale.id === userPreferredLocaleId
            )
            return userPreferredLocale
        })

        if (!userPreferredLocale) {
            userPreferredLocaleIds.find((userPreferredLocaleId) => {
                userPreferredLocale = supportedLocales.find(
                    (locale) => locale.languageFallbackFor === userPreferredLocaleId
                )
                return userPreferredLocale
            })
        }
        if (userPreferredLocale) {
            updateLocale(userPreferredLocale)
            return true
        }
    }
    return false
}

export const SelectRoleModalContext = React.createContext()
export const useSelectRoleContext = () => useContext(SelectRoleModalContext)
export const SelectRoleModalProvider = ({children}) => {
    const selectRoleModal = useSelectRoleModal()
    return (
        <SelectRoleModalContext.Provider value={selectRoleModal}>
            {children}
            <SelectRoleModal />
        </SelectRoleModalContext.Provider>
    )
}
SelectRoleModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const SelectRoleModal = () => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {isOpen, isOpening, onClose, isEdit, onFinishOpening, firstLinkTile, secondLinkTile} =
        useSelectRoleContext()
    const {site, buildUrl} = useMultiSite()
    const styles = useMultiStyleConfig('SelectRoleModal')
    const size = useBreakpointValue({base: 'sm', md: '2xl', xl: '4xl'})

    // custom scroll block on body when modal is open, as modal default also blocks scroll on cmp modal that can overlay this one
    const setOverflowOnBody = (value = '') => {
        document.querySelector('body').style.overflow = value
    }

    useEffect(() => {
        if (isOpen) {
            setOverflowOnBody('hidden')
        } else {
            setOverflowOnBody()
        }
        return () => {
            setOverflowOnBody()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isServer) {
            if (isOpening) {
                // TODO: Change this to `useLocalStorage` hook when localStorage detection is more robust
                const selectedRole = window.localStorage.getItem(SELECTED_ROLE_KEY)

                if (selectedRole && !isEdit) {
                    if (firstLinkTile?.role === selectedRole) {
                        onClose()
                        navigate(generalUrlBuilder(firstLinkTile))
                    } else if (secondLinkTile?.role === selectedRole) {
                        onClose()
                        navigate(generalUrlBuilder(secondLinkTile))
                    } else {
                        // invalid role
                        onFinishOpening()
                    }
                } else {
                    if (
                        !updateLocaleWithUserPreferredLocale({
                            site,
                            updateLocale: (newLocale) => {
                                let newUrl = getPathWithLocale(newLocale.id, buildUrl, {
                                    disallowParams: ['refine']
                                })
                                if (newUrl.indexOf('?') === -1) {
                                    newUrl += '?'
                                } else {
                                    newUrl += newUrl.indexOf('?') === newUrl.length - 1 ? '' : '&'
                                }
                                newUrl += 'locale=' + encodeURIComponent(newLocale.id)
                                window.location = newUrl
                            }
                        })
                    ) {
                        onFinishOpening()
                    }
                }
            }
        }
    }, [isOpening])
    const updateSelectedRole = (role) => {
        // TODO: Update when localStorage detection is more robust? useLocalStorage is only a getter
        if (ALLOWED_ROLES.indexOf(role) !== -1) {
            const priorSelectedRole = window.localStorage.getItem(SELECTED_ROLE_KEY)
            if (priorSelectedRole === role) {
                // role unchanged, close modal
                onClose()
                return
            }
            window.localStorage.setItem(SELECTED_ROLE_KEY, role)
        }
    }

    if (!firstLinkTile && !secondLinkTile) {
        return null
    }
    const headline = {
        line1: formatMessage(SELECT_ROLE_HEADLINE_LINE1)
    }

    return (
        <Modal
            size={size}
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="inside"
            isCentered
            closeOnOverlayClick={false}
            blockScrollOnMount={false}
        >
            <ModalOverlay />
            {!isOpening && (
                <ModalContent
                    {...styles?.modalContent}
                    containerProps={{'data-testid': 'select-role-modal'}}
                >
                    <ModalHeader {...styles?.modalHeader}>
                        <Text as="div">{headline.line1}</Text>
                    </ModalHeader>
                    {isEdit && <ModalCloseButton top="4" right="4" />}
                    <ModalBody {...styles?.modalBody}>
                        <Flex
                            flexDirection={{base: 'column', md: 'row'}}
                            justifyContent="space-between"
                            paddingBottom={{base: '4', lg: '8'}}
                        >
                            {firstLinkTile && (
                                <Box {...styles?.linkTileContainer}>
                                    <LinkTile
                                        {...firstLinkTile}
                                        onClick={() => updateSelectedRole(firstLinkTile.role)}
                                        variant="selectRole"
                                    ></LinkTile>
                                </Box>
                            )}
                            {secondLinkTile && (
                                <Box {...styles?.linkTileContainer}>
                                    <LinkTile
                                        {...secondLinkTile}
                                        onClick={() => updateSelectedRole(secondLinkTile.role)}
                                        variant="selectRole"
                                    ></LinkTile>
                                </Box>
                            )}
                        </Flex>
                    </ModalBody>
                </ModalContent>
            )}
        </Modal>
    )
}

export const useGetSelectRoleLinkTiles = () => {
    const {data: selectRolePage} = usePage({parameters: {pageId: 'selectrole'}})
    const navTree = convertPageToNavigationTree({page: selectRolePage, createCombindeCol: false})
    const firstLinkTile =
        navTree?.firstCol?.categories?.length > 0
            ? setLinkTileRole(navTree?.firstCol?.categories?.[0])
            : null
    const secondLinkTile =
        navTree?.secondCol?.categories?.length > 0
            ? setLinkTileRole(navTree?.secondCol?.categories?.[0])
            : null
    return {
        firstLinkTile,
        secondLinkTile
    }
}

export const useSelectRoleModal = () => {
    const {firstLinkTile, secondLinkTile} = useGetSelectRoleLinkTiles()

    const [state, setState] = useState({
        isOpen: false,
        isOpening: false,
        isEdit: false,
        firstLinkTile,
        secondLinkTile
    })
    const {pathname} = useLocation()

    useEffect(() => {
        if (state.isOpen) {
            setState({
                ...state,
                isOpen: false,
                isOpening: false
            })
        }
    }, [pathname])

    return {
        isOpen: state.isOpen,
        isEdit: state.isEdit,
        isOpening: state.isOpening,
        firstLinkTile: state.firstLinkTile,
        secondLinkTile: state.secondLinkTile,
        onOpen: (data) => {
            setState({
                ...state,
                isOpen: true,
                isOpening: true,
                ...data
            })
        },
        onFinishOpening: () => {
            setState({
                ...state,
                isOpen: true,
                isOpening: false
            })
        },
        onClose: () => {
            setState({
                ...state,
                isOpen: false,
                isOpening: false,
                isEdit: false
            })
        }
    }
}

export default SelectRoleModal
